import { createApi } from '@reduxjs/toolkit/query/react'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { SessionFilter, SessionQuickFilter } from '../../components/SessionFilters/model'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_API_BASE_URL!

export interface SessionFilterRequest extends BasePageRequest {
  filterType: string
  userId?: string
}

export interface CreateSessionFilterRequest extends BaseRequest {
  name: string
  isDefault: boolean
  filterType: string
  filter: SessionFilter
}

export interface UpdateSessionFilterRequest extends BaseRequest {
  filterId: string
  name?: string
  isDefault?: boolean
  filter?: SessionFilter
}

export interface DeleteSessionFilterRequest extends BaseRequest {
  filterId: string
}

export const quickFilter = createApi({
  reducerPath: 'quickFilter',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['SessionFilters'],
  endpoints: (builder) => ({
    createSessionFilter: builder.mutation<BaseResponse<SessionQuickFilter>, CreateSessionFilterRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/session-filters/`,
          method: 'POST',
          body: {
            name: request.name,
            isDefault: request.isDefault,
            filter: request.filter,
            filterType: request.filterType,
          },
        }
      },
      invalidatesTags: ['SessionFilters'],
    }),
    updateSessionFilter: builder.mutation<BaseResponse<SessionQuickFilter>, UpdateSessionFilterRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/session-filters/${request.filterId}`,
          method: 'PATCH',
          body: {
            name: request.name,
            isDefault: request.isDefault,
            filter: request.filter,
          },
        }
      },
      invalidatesTags: ['SessionFilters'],
    }),
    deleteSessionFilter: builder.mutation<void, DeleteSessionFilterRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/session-filters/${request.filterId}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['SessionFilters'],
    }),
    getSessionFilters: builder.query<BasePageResponse<SessionQuickFilter[]>, SessionFilterRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/session-filters/`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            filterType: request.filterType,
            userId: request.userId,
          },
        }
      },
      providesTags: ['SessionFilters'],
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetSessionFiltersQuery, useCreateSessionFilterMutation, useUpdateSessionFilterMutation, useDeleteSessionFilterMutation } = quickFilter
